/**
 * **Selling units** (handling units) describe how a SKU is sold: e.g. one “Unit”,
 * one “Inner” (4 base tiles), or a **fractional** sub-unit (e.g. 0.25 base unit).
 *
 * The backend stores the scale on `wl.product.handling.unit` as
 * `multiplier_from_base_unit` (preferred) and legacy `unit_quantity` (int).
 * Catalog JSON exposes `multiplier_from_base_unit` plus deprecated `quantity`,
 * which should mirror the multiplier after parser changes.
 *
 * **Relations (frontend):**
 * - `IProductHandlingUnit` on catalog/order DTOs (see `products.models.ts`).
 * - Quote/order lines: base amount ≈ `quantity_handling_unit * handlingUnitBaseMultiplier(selectedHu)`;
 *   must match Odoo `product_uom_qty` for the same line.
 *
 * @param hu - Handling unit object from API or mapped row; may be null when none selected.
 * @returns Base units represented by **one** selling unit; `1` if unknown (neutral multiplier).
 * @see `SaleOrder._hu_base_units_per_selling_unit` in wolsal-odoo `quotation.py`
 */
export function handlingUnitBaseMultiplier(
  hu: { multiplier_from_base_unit?: number | null; quantity?: number | null } | null | undefined
): number {
  if (!hu) return 1;
  const m = hu.multiplier_from_base_unit;
  if (m != null && Number(m) > 0) return Number(m);
  const q = hu.quantity;
  if (q != null && Number(q) > 0) return Number(q);
  return 1;
}

/**
 * Applies the rounding rule defined on a handling unit to a quantity value.
 * Intended for rounding base-unit quantities using the base unit's config.
 * Returns `quantity` unchanged when `hu` has no rounding settings.
 */
export function applyHandlingUnitRounding(
  quantity: number,
  hu: { rounding_mode?: string | null; rounding_decimals?: number | null } | null | undefined
): number {
  if (!hu || hu.rounding_mode == null || hu.rounding_decimals == null) return quantity;
  const factor = Math.pow(10, hu.rounding_decimals);
  switch (hu.rounding_mode) {
    case 'ROUND_UP':
    case 'ROUND_CEILING':
      return Math.ceil(quantity * factor) / factor;
    case 'ROUND_DOWN':
    case 'ROUND_FLOOR':
      return Math.floor(quantity * factor) / factor;
    case 'ROUND_HALF_UP':
      return Math.round(quantity * factor) / factor;
    case 'ROUND_HALF_DOWN':
      return Math.floor(quantity * factor + 0.5) / factor;
    case 'ROUND_HALF_EVEN': {
      const shifted = quantity * factor;
      const floor = Math.floor(shifted);
      return (Math.abs(shifted - floor - 0.5) < Number.EPSILON
        ? (floor % 2 === 0 ? floor : floor + 1)
        : Math.round(shifted)) / factor;
    }
    default:
      return quantity;
  }
}

function formatQtyForTooltip(v: number): string {
  if (!Number.isFinite(v)) return '0';
  const r = Math.round(v * 1e6) / 1e6;
  return Number.isInteger(r) ? String(r) : String(r);
}

/**
 * Returns true only when the selling unit has a known, positive multiplier —
 * i.e. conversion to base units is meaningful and can be displayed.
 *
 * Opaque units (Panaria-style: `multiplier_from_base_unit = 0`) return false,
 * preventing a misleading "× 1 base units" tooltip from appearing.
 */
export function handlingUnitHasConversion(
  hu: { multiplier_from_base_unit?: number | null; quantity?: number | null } | null | undefined
): boolean {
  if (!hu) return false;
  const m = hu.multiplier_from_base_unit;
  if (m != null && Number(m) > 0) return true;
  const q = hu.quantity;
  return q != null && Number(q) > 0;
}

/**
 * Params for i18n key `quotations_and_orders.side_by_side_panel.quantity_in_base_units`:
 * base total = selling-unit count × multiplier (base units per selling unit).
 */
export function quantityInBaseUnitsTranslationParams(
  quantityHandlingUnit: number | null | undefined,
  hu: { multiplier_from_base_unit?: number | null; quantity?: number | null } | null | undefined,
  baseUnit?: { rounding_mode?: string | null; rounding_decimals?: number | null } | null
): { sellingQty: string; multiplier: string; baseUnits: string } {
  const mult = handlingUnitBaseMultiplier(hu);
  const qtyHu = Number(quantityHandlingUnit ?? 0);
  const base = applyHandlingUnitRounding(qtyHu * mult, baseUnit);
  return {
    sellingQty: formatQtyForTooltip(qtyHu),
    multiplier: formatQtyForTooltip(mult),
    baseUnits: formatQtyForTooltip(base),
  };
}
